import { HerdSettingsDto, MobiLinkAnimalDto, ParamCalcDto, ParamIntDto, SaveBarcodesCommand, SaveMobiLinkSettingsCommand, SentBarcodeDto, UpdatePregnancyCheckOrdersCommand, UserHerdDto, UserSettingsDto } from "../generated/tsClient";
import i18n from "../i18n";
import {
    setAnimalList, setBarcodesSendToOptions, setCowListOrderOptions,
    setError, setHerdCanOrderPregCheck, setInsemDaysHighlightLimits,
    setLanguage, setSelectedBarcodesSendTo, setSentBarcodes,
    setUserCanSendToMMfi, setUserHerds, setUserIsAuthorized,
    setAnimalListLoaded, setShowPregCheckMessage
} from "../store/slices/appSlice";
import { store } from "../store/store";
import { sendGetRequest, sendPatchRequest, sendPostRequest } from "../utils/axiosClient";

export async function fetchUserIsAuthorized(): Promise<boolean> {
    const response = await sendGetRequest(`api/v1/home/checkUserIsAuthorized`, true)
        .catch(error => {
            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    if(!response.data)
        store.dispatch(setError(i18n.t('Errors.NoRights')));

    store.dispatch(setUserIsAuthorized(response.data));

    return response.data;
}

export async function fetchUserHerds(): Promise<UserHerdDto[]> {
    const response = await sendGetRequest('api/v1/home/getUserHerds', true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setUserHerds(response.data));

    return response.data;
}

export async function fetchAnimalList(herdId: number): Promise<MobiLinkAnimalDto[]> {
    const response = await sendGetRequest(`api/v1/home/getAnimalList?herdId=${herdId}`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    if (response.data.length === 0) {
        store.dispatch(setError(i18n.t('Errors.DataNotFound')));
    }

    store.dispatch(setAnimalList(response.data));
    store.dispatch(setAnimalListLoaded(new Date()));

    return response.data;
}

export async function fetchUserSettings(): Promise<UserSettingsDto> {
    const response = await sendGetRequest(`api/v1/home/getUserSettings`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setLanguage(response.data.language));
    store.dispatch(setUserCanSendToMMfi(response.data.userCanSendToMMfi));

    return response.data;
}

export async function fetchHerdSettings(herdId: number): Promise<HerdSettingsDto> {
    const response = await sendGetRequest(`api/v1/home/getHerdSettings?herdId=${herdId}`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setSelectedBarcodesSendTo(response.data.sendTo));
    store.dispatch(setHerdCanOrderPregCheck(response.data.canOrderPregCheck));
    store.dispatch(setShowPregCheckMessage(response.data.showPregCheckMessage));

    return response.data;
}

export async function fetchCowListOrderOptions(language: string): Promise<ParamIntDto[]> {
    const response = await sendGetRequest(`api/v1/home/getParamInt?paramAttName=CowListOrder&language=${language}`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setCowListOrderOptions(response.data));

    return response.data;
}

export async function fetchBarcodesSendToOptions(language: string): Promise<ParamIntDto[]> {
    const response = await sendGetRequest(`api/v1/home/getParamInt?paramAttName=BarcodesSendTo&language=${language}`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setBarcodesSendToOptions(response.data));

    return response.data;
}

export async function fetchInsemDaysHiglightLimits(): Promise<ParamCalcDto> {
    const response = await sendGetRequest(`api/v1/home/getParamCalc?calcGroup=MKNANALIM&calcName=InseminationDays&CalcCode=1`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setInsemDaysHighlightLimits(response.data[0]));

    return response.data;
}

export async function fetchSentBarcodes(herdId: number): Promise<SentBarcodeDto[]> {
    const response = await sendGetRequest(`api/v1/home/getSentBarcodes?herdId=${herdId}`, true)
        .catch(error => {
            if (error.response?.status === 403) {
                store.dispatch(setError(i18n.t('Errors.NoRights')));
                return Promise.reject();
            }

            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    store.dispatch(setSentBarcodes(response.data));

    return response.data;
}

export async function saveSettings(command: SaveMobiLinkSettingsCommand): Promise<void> {
    const response = await sendPostRequest(
        `api/v1/home/saveSettings`,
        command,
        "application/json",
        true
    ).catch((error) => {
        if (error.response?.status === 403) {
            store.dispatch(setError(i18n.t('Errors.NoRights')));
            return Promise.reject();
        }

        store.dispatch(setError(i18n.t("Errors.InternalError")));
        return Promise.reject();
    });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t("Errors.InternalError")));
        return Promise.reject();
    }

    return response.data;
}

export async function saveBarcodes(command: SaveBarcodesCommand): Promise<void> {
    const response = await sendPostRequest(
        `api/v1/home/saveBarcodes`,
        command,
        "application/json",
        true
    ).catch((error) => {
        if (error.response?.status === 403) {
            store.dispatch(setError(i18n.t('Errors.NoRights')));
            return Promise.reject();
        }

        if (error.response?.status === 500) {
            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        }

        store.dispatch(setError(i18n.t("Errors.NoAnimalsSend")));
        return Promise.reject();
    });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status === 500) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t("Errors.NoAnimalsSend")));
        return Promise.reject();
    }

    return response.data;
}

export async function saveSentBarcodes(command: UpdatePregnancyCheckOrdersCommand): Promise<void> {
    const response = await sendPatchRequest(
        `api/v1/home/updatePregCheckOrders`,
        command,
        "application/json",
        true
    ).catch((error) => {
        if (error.response?.status === 403) {
            store.dispatch(setError(i18n.t('Errors.NoRights')));
            return Promise.reject();
        }

        if (error.response?.status === 500) {
            store.dispatch(setError(i18n.t('Errors.InternalError')));
            return Promise.reject();
        }

        store.dispatch(setError(i18n.t("Errors.NoAnimalsSend")));
        return Promise.reject();
    });

    if (response.status === 403) {
        store.dispatch(setError(i18n.t('Errors.NoRights')));
        return Promise.reject();
    }

    if (response.status === 500) {
        store.dispatch(setError(i18n.t('Errors.InternalError')));
        return Promise.reject();
    }

    if (response.status !== 200) {
        store.dispatch(setError(i18n.t("Errors.NoAnimalsSend")));
        return Promise.reject();
    }

    return response.data;
}